// @flow
import { postJson, getJson } from 'utils/http';
import type {
  HeroSearch,
  Page,
  Sidebar,
  Category,
  ButtonLinkData,
  CmsTag,
  Author
} from 'api/cms';
import type { Address } from 'api/orgs';
import type { ImageData } from 'api/shared';

export type MiniPage = {
  updated: string,
  title: string,
  id: string,
  type: 'PAGE' | 'POST',
  translations: Array<MiniPage>,
  url: string,
  locale: string
};

export type MiniSubsite = {
  id: string,
  homePage: MiniPage,
  publicName: string
};

export type Subsite = {
  created: string,
  defaultSearchType: ListingType,
  defaultSidebarId: string,
  defaultLocale: string,
  homePage: MiniPage,
  id: string,
  includedSubsites: Array<MiniSubsite>,
  urlRoot: string,
  visibleToRobots: boolean,
  placeholderImages: Array<ImageData>,
  publicName: string,
  canSubscribe: boolean,
  searchLinks: Array<ButtonLinkData>
};

// @NOTE: At some point it may make
// sense to create a totally separate
// type, but the differences that exist
// (e.g. no sidebarId, authors doesn't
// have a created field, etc.) in the public page are
// unlikely to cause issues currently.
export type PublicPage = {
  ...$Exact<Page>,
  heroSearch?: HeroSearch,
  sidebar: Sidebar,
  subsite: Subsite,
  categories: Array<Category>
};

export function fetchPage(pageUrl: string): Promise<Page> {
  return getJson(`/data/website/contents/${pageUrl}`).then(({ data }) => data);
}

// Used in e.g., four article highlight block
export type MiniMiniPage = {
  title: string,
  id: string,
  url: string,
  image: ?ImageData
};

export type FourArticlesHighlightWebsiteData = {|
  headline: ?string,
  pageOne: ?MiniMiniPage,
  pageTwo: ?MiniMiniPage,
  pageThree: ?MiniMiniPage,
  pageFour: ?MiniMiniPage,
  buttonUrl: ?string,
  buttonText: ?string
|};

export type GradFairGradProgramLinksBlockData = {|
  programs: Array<{
    id: string,
    name: string,
    schoolName: string,
    logo: ImageData,
    address: Address,
    url: { [UserLocale]: string }
  }>
|};

export function getRecommendations(
  listingId: string,
  listingType: ListingTypeConcrete
): Promise<Array<string>> {
  if (listingType !== 'JOB') return Promise.resolve([]);
  return getJson(`/data/website/jobs/${listingId}/recommendations`).then(
    ({ jobIds }) => jobIds
  );
}

export type ShortUrlType = ListingTypeConcrete | 'CMS_PAGE' | 'URL';

export function getShortUrl({
  toType,
  toId,
  toUrl,
  extraParams = []
}: {
  toType: ShortUrlType,
  toId?: string,
  toUrl?: string,
  extraParams?: Array<{ name: string, value: string }>
}) {
  return postJson('/data/short-urls', {
    toType,
    toId,
    toUrl,
    extraParams,
    locale: USER_LOCALE
  }).then(({ url }) => url);
}

export function fetchTag(slug: string, subsiteId: string): Promise<CmsTag> {
  return getJson(`/data/website/subsites/${subsiteId}/tags/${slug}`).then(
    ({ tag }) => tag
  );
}

export function fetchAuthor(slug: string): Promise<Author> {
  return getJson(`/data/website/authors/${slug}`).then(({ author }) => author);
}

export function fetchSubsite(subsiteId: string): Promise<Subsite> {
  return getJson(`/data/website/subsites/${subsiteId}`).then(
    ({ subsite }) => subsite
  );
}
